/**
 * الدخول / إنشاء حساب.
 *
 * الطريقة: رمز لمرة واحدة على الجوال (آلية Bitwere SaaS Core). لا كلمة مرور.
 * في التطوير المحلي يُعرض الرمز على الشاشة لأن الـCore لا يرسل رسالة فعلية
 * إلا عند ضبط Twilio.
 */

import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { color } from '@/design/tokens';
import { Button, Card, SegmentedControl } from '@/components/primitives/Primitives';
import { useAuth } from '@/services/auth';

type Mode = 'login' | 'signup';

export default function AuthRoute(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestCode, verifyCode, status } = useAuth();
  const redirectTo = (location.state as { from?: string } | null)?.from ?? '/watchlist';

  const [mode, setMode] = useState<Mode>('login');
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [devCode, setDevCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (status === 'authenticated') {
    navigate(redirectTo, { replace: true });
  }

  async function submitPhone(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const res = await requestCode(phone);
      setDevCode(res.devCode ?? null);
      setStep('code');
    } catch {
      setError('رقم الجوال غير صالح أو تعذّر إرسال الرمز.');
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await verifyCode(phone, code, mode === 'signup' ? name : undefined);
      navigate(redirectTo, { replace: true });
    } catch {
      setError('الرمز غير صحيح أو انتهت صلاحيته.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="nq-enter" style={{ paddingTop: 44, maxWidth: 460, margin: '0 auto' }}>
      <h1 style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-.02em' }}>
        {mode === 'signup' ? 'إنشاء حساب' : 'تسجيل الدخول'}
      </h1>
      <p style={{ marginTop: 10, fontSize: 13.5, color: color.textSubtle, lineHeight: 1.7 }}>
        الدخول برمز يصل إلى جوالك — بلا كلمة مرور.
      </p>

      <Card style={{ marginTop: 22 }}>
        <SegmentedControl
          options={[
            { value: 'login' as Mode, label: 'تسجيل الدخول' },
            { value: 'signup' as Mode, label: 'إنشاء حساب' },
          ]}
          value={mode}
          onChange={(m) => {
            setMode(m);
            setStep('phone');
            setError(null);
          }}
          ariaLabel="نوع الدخول"
        />

        {step === 'phone' ? (
          <div style={{ marginTop: 20 }}>
            {mode === 'signup' ? (
              <div style={{ marginBottom: 16 }}>
                <label htmlFor="name" className="nq-label" style={{ display: 'block', marginBottom: 8 }}>
                  الاسم
                </label>
                <input
                  id="name"
                  className="nq-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="الاسم الكامل"
                />
              </div>
            ) : null}

            <label htmlFor="phone" className="nq-label" style={{ display: 'block', marginBottom: 8 }}>
              رقم الجوال
            </label>
            <input
              id="phone"
              className="nq-input nq-num"
              dir="ltr"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="05XXXXXXXX"
            />
            <div style={{ marginTop: 20 }}>
              <Button block onClick={submitPhone} disabled={busy || phone.trim().length < 9}>
                {busy ? 'جارٍ الإرسال...' : 'أرسل الرمز'}
              </Button>
            </div>
          </div>
        ) : (
          <div style={{ marginTop: 20 }}>
            <label htmlFor="code" className="nq-label" style={{ display: 'block', marginBottom: 8 }}>
              رمز التحقق
            </label>
            <input
              id="code"
              className="nq-input nq-num"
              dir="ltr"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
            />
            {devCode ? (
              <p
                data-testid="dev-code"
                style={{
                  marginTop: 12,
                  padding: '10px 12px',
                  borderRadius: 10,
                  background: 'rgba(240,162,75,.08)',
                  border: '1px solid rgba(240,162,75,.28)',
                  fontSize: 12.5,
                  color: color.amberText,
                }}
              >
                بيئة تطوير محلية — الرمز:{' '}
                <span className="nq-num" dir="ltr">
                  {devCode}
                </span>
              </p>
            ) : null}
            <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button onClick={submitCode} disabled={busy || code.trim().length < 4}>
                {busy ? 'جارٍ التحقق...' : 'تأكيد'}
              </Button>
              <Button variant="ghost" onClick={() => setStep('phone')}>
                تغيير الرقم
              </Button>
            </div>
          </div>
        )}

        {error ? (
          <p role="alert" style={{ marginTop: 14, fontSize: 12.5, color: color.red }}>
            {error}
          </p>
        ) : null}
      </Card>

      <p style={{ marginTop: 18, fontSize: 12, color: color.micro, lineHeight: 1.7 }}>
        {/* لا نَعِد بميزة لم تُطلق: التنبيهات والتقارير قيد الإطلاق، فلا
            تُذكر كمقابل لتسجيل الدخول. */}
        بدون حساب تستطيع تصفّح البيانات العامة والخريطة وصفحات الأحياء. الحساب يلزم لحفظ
        قائمة الأحياء التي تتابعها. التنبيهات والتقارير قيد الإطلاق ولم تُفعّل بعد.
      </p>
    </div>
  );
}
